import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { hubAPI } from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    ArrowLeft, FolderKanban, Target, TimerOff, AlertTriangle, CheckCircle2, ChevronRight, Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const FILTERS = {
    active: {
        icon: FolderKanban,
        label: 'Projets actifs',
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        subtitle: 'Tous les projets en cours (tous espaces, tous thèmes).',
    },
    mine: {
        icon: Target,
        label: 'Mes projets',
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        subtitle: 'Projets où vous êtes membre — vos responsabilités actives.',
    },
    stage_blocked: {
        icon: TimerOff,
        label: 'En retard',
        color: 'text-red-600',
        bg: 'bg-red-50',
        subtitle: "Projets dont l'étape actuelle a dépassé sa durée max (paramétrée par étape). À débloquer en priorité.",
    },
    overdue: {
        icon: AlertTriangle,
        label: 'Deadline passée',
        color: 'text-slate-500',
        bg: 'bg-slate-100',
        subtitle: 'Info : projets actifs dont la deadline globale est passée (ne déclenche pas d’alerte « en retard » — voir « En retard » pour ça).',
    },
    closed: {
        icon: CheckCircle2,
        label: 'Projets clôturés',
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        subtitle: 'Projets terminés (gagnés ou clôturés).',
    },
};

export default function HubProjectsList() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const filter = params.get('filter') || 'active';
    const meta = FILTERS[filter] || FILTERS.active;
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const r = await hubAPI.listAllProjects(filter);
                if (alive) setProjects(Array.isArray(r.data) ? r.data : []);
            } catch {
                if (alive) setProjects([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [filter]);

    const Icon = meta.icon;

    return (
        <DashboardLayout>
            <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-testid="hub-projects-list">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/projets')} data-testid="btn-back-projets" className="gap-1.5">
                        <ArrowLeft className="w-4 h-4" /> Projets
                    </Button>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                    <span className="text-sm text-slate-500">{meta.label}</span>
                </div>

                <div className="flex items-center gap-4">
                    <div className={cn('w-14 h-14 rounded-2xl flex items-center justify-center', meta.bg)}>
                        <Icon className={cn('w-7 h-7', meta.color)} />
                    </div>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-slate-900">{meta.label}</h1>
                        <p className="text-sm text-slate-500 mt-0.5">{meta.subtitle}</p>
                    </div>
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-sm font-semibold" data-testid="list-count">
                        {loading ? '…' : `${projects.length} projet${projects.length > 1 ? 's' : ''}`}
                    </Badge>
                </div>

                {/* Onglets de filtres rapides */}
                <div className="flex flex-wrap gap-2">
                    {Object.entries(FILTERS).map(([key, m]) => (
                        <Link
                            key={key}
                            to={`/projets/liste?filter=${key}`}
                            data-testid={`tab-filter-${key}`}
                            className={cn(
                                'text-xs px-3 py-1.5 rounded-full border transition-colors',
                                key === filter
                                    ? 'bg-slate-900 text-white border-slate-900'
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
                            )}
                        >
                            {m.label}
                        </Link>
                    ))}
                </div>

                {/* Liste */}
                {loading ? (
                    <Card><CardContent className="p-10 text-center text-sm text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
                    </CardContent></Card>
                ) : projects.length === 0 ? (
                    <Card><CardContent className="p-10 text-center text-sm text-slate-500" data-testid="list-empty">
                        Aucun projet dans cette catégorie pour le moment.
                    </CardContent></Card>
                ) : (
                    <div className="space-y-2" data-testid="projects-list">
                        {projects.map((p) => (
                            <ProjectRow key={p.id} project={p} filter={filter} />
                        ))}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}

function ProjectRow({ project, filter }) {
    const navigate = useNavigate();
    const stageInfo = useMemo(() => {
        const pipeline = project.pipeline || [];
        return pipeline.find((s) => s.id === project.current_stage_id) || null;
    }, [project]);
    const deadline = project.deadline ? new Date(project.deadline) : null;
    const deadlinePast = deadline && deadline < new Date();

    return (
        <button
            type="button"
            onClick={() => navigate(`/projets/projet/${project.id}`)}
            className="w-full text-left rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md hover:border-indigo-300 transition-all flex items-start gap-4"
            data-testid={`project-row-${project.id}`}
        >
            {/* Espace + importance */}
            <div className="shrink-0 flex flex-col items-center gap-1">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 font-semibold text-xs">
                    {project.space?.name?.substring(0, 2)?.toUpperCase() || 'PR'}
                </div>
                {typeof project.importance === 'number' && project.importance > 0 && (
                    <span className="text-[10px] text-amber-600 font-semibold">★ {project.importance}</span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-slate-900 truncate">{project.name}</div>
                    {project.status === 'won' && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Gagné</Badge>}
                    {project.status === 'lost' && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]">Clôturé</Badge>}
                </div>
                {project.description && (
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{project.description}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                    {project.space?.name && (
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            {project.space.name}
                        </span>
                    )}
                    {stageInfo && (
                        <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                            {stageInfo.name}
                        </span>
                    )}
                    {filter === 'stage_blocked' && project._days_on_stage != null && (
                        <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium">
                            En retard +{project._days_on_stage - project._stage_max_days}j (étape max {project._stage_max_days}j)
                        </span>
                    )}
                    {deadline && (
                        <span className={cn(
                            'px-2 py-0.5 rounded-full',
                            deadlinePast ? 'bg-amber-50 text-amber-700 font-medium' : 'bg-slate-100 text-slate-600',
                        )}>
                            {deadlinePast ? 'Deadline passée : ' : 'Deadline : '}{deadline.toLocaleDateString('fr-FR')}
                        </span>
                    )}
                </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-300 shrink-0 mt-1" />
        </button>
    );
}
